import { getFaviconUrl } from '@/utils/faviconCache';
import {
  Avatar,
  AvatarFallback,
  AvatarGroup,
  AvatarImage,
} from '@heidi/design-system/avatar';
import { typographyVariant } from '@heidi/design-system/typography';
import { CollectionIcon } from '@heidi/evidence';
import { cn } from '@/lib/cn';
import {
  BookOpen,
  LibraryBig,
  Stethoscope,
  type LucideIcon,
} from 'lucide-react';
import { type ReactNode } from 'react';
import { useIntl } from 'react-intl';
import {
  getBrandTextClass,
  selectPersonalisedSourceFace,
  type PersonalisedSource,
  type SourceTypeCategory,
} from './personalised-source-avatars.utils';
import { usePersonalisedSourceSummary } from './use-personalised-source-summary';

const WORK_AVATAR_CLASS = 'shrink-0 overflow-hidden';
const PRO_TIER_AVATAR_CLASSES = 'bg-tier-pro-muted text-tier-pro-text';

const CATEGORY_ICONS: Record<SourceTypeCategory, LucideIcon> = {
  clinicalWeb: Stethoscope,
  literatureGuidelines: BookOpen,
  myLibrary: LibraryBig,
};

interface PersonalisedSourceAvatarsProps {
  className?: string;
  /** Drops the trailing count when surrounding copy already states the scale. */
  hideScaleLabel?: boolean;
  /** Keeps each surface's avatar presentation independent of selection. */
  variant?: 'evidence' | 'work';
  /**
   * Drawn while no source resolves yet — first paint, before integrations and
   * collections land. A trigger whose whole face is the stack would otherwise
   * render as an empty box.
   */
  fallback?: ReactNode;
}

/** Trailing logos/icons for the personalised Sources button. */
export const PersonalisedSourceAvatars = ({
  className,
  hideScaleLabel = false,
  variant = 'evidence',
  fallback,
}: PersonalisedSourceAvatarsProps) => {
  const intl = useIntl();
  const {
    summary,
    selectionTotal,
    partnerSources,
    defaultSources,
    librarySources,
  } = usePersonalisedSourceSummary(true, variant);

  const { items, trailingLabel } = selectPersonalisedSourceFace(
    summary,
    selectionTotal,
    partnerSources,
    defaultSources,
    librarySources,
    variant,
    hideScaleLabel
  );
  const isTypeIcons =
    variant === 'evidence' &&
    !summary.isDefault &&
    partnerSources.length === 0 &&
    librarySources.length === 0;
  const visibleAvatars: ReactNode[] = items.map((item) =>
    item.kind === 'source' ? (
      <SourceAvatar key={item.source.key} source={item.source} variant={variant} />
    ) : variant === 'evidence' ? (
      <ModeIcon key={`type:${item.category}`} category={item.category} />
    ) : (
      <CategoryAvatar key={`type:${item.category}`} category={item.category} />
    )
  );

  if (visibleAvatars.length === 0) return <>{fallback}</>;
  const ariaLabel = summary.isDefault
    ? intl.formatMessage({
        defaultMessage:
          'Your personalised sources · searches 7,000+ clinical sources',
        id: 'kP9mRw2nVx',
      })
    : intl.formatMessage(
        {
          defaultMessage:
            '{count, plural, one {# source selected} other {# sources selected}}',
          id: '7XKoIcUupt',
          description:
            'Personalised Sources button — accessible label for the selected source count',
        },
        { count: selectionTotal }
      );

  return (
    <div
      className={cn(
        'flex items-center',
        variant === 'evidence' && 'gap-1',
        className
      )}
      aria-label={ariaLabel}
    >
      {isTypeIcons ? (
        <span className="inline-flex items-center gap-1">{visibleAvatars}</span>
      ) : (
        <AvatarGroup
          className={cn(
            'items-center',
            variant === 'evidence' &&
              !isTypeIcons &&
              // Trigger bg is state-dependent; fixed ringBackground can't track it.
              '[&>[data-slot=avatar]]:ring-0'
          )}
        >
          {visibleAvatars}
        </AvatarGroup>
      )}
      {trailingLabel !== null && (
        <span
          className={cn(
            typographyVariant({ variant: 'p2Bold' }),
            variant === 'work' ? 'text-text-secondary' : 'text-text-primary',
            variant === 'work' && 'self-center'
          )}
        >
          {trailingLabel}
        </span>
      )}
    </div>
  );
};

function SourceAvatar({
  source,
  variant,
}: {
  source: PersonalisedSource;
  variant: 'evidence' | 'work';
}) {
  const isCollection = source.iconName !== undefined;
  const src =
    source.logoUrl ?? (source.domain ? getFaviconUrl(source.domain) : undefined);

  return (
    <Avatar
      size="xs"
      className={variant === 'work' ? WORK_AVATAR_CLASS : undefined}
    >
      {src && (
        <AvatarImage
          className={variant === 'work' ? 'size-full object-cover' : undefined}
          src={src}
          alt=""
        />
      )}
      <AvatarFallback
        className={
          isCollection
            ? PRO_TIER_AVATAR_CLASSES
            : cn(
                getBrandTextClass(source.brandColor),
                !source.brandColor && 'bg-tier-pro',
                typographyVariant({ variant: 'footnoteBold' })
              )
        }
        style={
          source.brandColor ? { backgroundColor: source.brandColor } : undefined
        }
      >
        {isCollection ? (
          <CollectionIcon icon={source.iconName} size={12} />
        ) : (
          source.displayName.charAt(0).toUpperCase()
        )}
      </AvatarFallback>
    </Avatar>
  );
}

function ModeIcon({ category }: { category: SourceTypeCategory }) {
  const Icon = CATEGORY_ICONS[category];
  return (
    <span className="inline-flex items-center justify-center text-text-primary">
      <Icon size={16} />
    </span>
  );
}

function CategoryAvatar({ category }: { category: SourceTypeCategory }) {
  const Icon = CATEGORY_ICONS[category];
  return (
    <Avatar size="xs" className={WORK_AVATAR_CLASS}>
      <AvatarFallback
        className="bg-surface-hover text-text-secondary"
        icon={
          <Icon
            className="stroke-icon-xs text-text-secondary"
            aria-hidden
          />
        }
      />
    </Avatar>
  );
}
